// Gate admin (central "Gate - Manage gates & guards" permission): the gates of the admin's
// locations, their guards, and (read-only) the stores subscribed to each. Stores subscribe
// themselves from their operating profile in the Store App.

import { useCallback, useEffect, useState } from "react";
import { DoorOpen, Loader2, Pencil, Plus, Search, Store, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type AdminGate, type AdminGatesResponse, type UserHit } from "@/lib/gate";
import { useMe } from "@/lib/me";
import { PageContainer } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

function msg(e: unknown, fallback: string) {
  return e instanceof ApiError ? e.message : fallback;
}

interface Draft {
  id?: number;
  operationUnitId: number | null;
  name: string;
  description: string;
  active: boolean;
}

export default function Admin() {
  const { token } = useAuth();
  const { me, reload: reloadMe } = useMe();
  const [data, setData] = useState<AdminGatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await gateApi.adminGates(token));
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 403
          ? "You do not have permission to manage gates. Ask an administrator for “Gate - Manage gates & guards”."
          : msg(e, "Could not load the gates."),
      );
    }
  }, [token]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial load
    void load();
  }, [load]);

  /** After any change: refresh this list and the user's own gate list (the picker / switcher). */
  const changed = useCallback(async () => {
    await load();
    reloadMe();
  }, [load, reloadMe]);

  async function saveDraft() {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await gateApi.saveGate(token, {
        id: draft.id,
        operationUnitId: draft.operationUnitId ?? undefined,
        name: draft.name,
        description: draft.description,
        active: draft.active,
      });
      if (r?.errorMessage) {
        toast.error(r.errorMessage);
        return;
      }
      toast.success(draft.id ? "Gate updated." : "Gate created.");
      setDraft(null);
      await changed();
    } catch (e) {
      toast.error(msg(e, "Could not save the gate."));
    } finally {
      setSaving(false);
    }
  }

  const ous = data?.operationUnits ?? [];

  return (
    <PageContainer className="gap-4">
      <p className="text-sm text-muted-foreground">
        Gates of your locations and the guards assigned to them. A guard signs in to this app and sees the expected
        deliveries of every store subscribed to their gate.
      </p>
      {data && ous.length > 0 && (
        <Button
          className="h-12 rounded-xl text-base md:w-fit md:px-6"
          onClick={() =>
            setDraft({ operationUnitId: ous.length === 1 ? ous[0].id : null, name: "", description: "", active: true })
          }
        >
          <Plus className="size-5" /> New gate
        </Button>
      )}

      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : !data ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : ous.length === 0 ? (
        <p className="text-sm text-muted-foreground">You have no locations to manage.</p>
      ) : data.gates.length === 0 ? (
        <Card>
          <CardContent className="text-sm text-muted-foreground">No gates yet — create the first one.</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {data.gates.map((g) => (
            <GateCard
              key={g.id}
              gate={g}
              showLocation={ous.length > 1}
              onEdit={() =>
                setDraft({
                  id: g.id,
                  operationUnitId: g.operationUnit?.id ?? null,
                  name: g.name,
                  description: g.description ?? "",
                  active: g.active,
                })
              }
              onChanged={changed}
            />
          ))}
        </div>
      )}

      {me && !me.isAdmin && data && (
        <p className="text-xs text-muted-foreground">Signed in without the gate-admin permission.</p>
      )}

      <Sheet open={draft != null} onOpenChange={(o) => !o && setDraft(null)}>
        <SheetContent side="bottom" className="flex max-h-[92svh] flex-col gap-0 rounded-t-2xl p-0">
          <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/30" aria-hidden />
          <SheetHeader className="pr-12">
            <SheetTitle className="text-lg">{draft?.id ? "Edit gate" : "New gate"}</SheetTitle>
          </SheetHeader>
          {draft && (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-2">
              {!draft.id && ous.length > 1 && (
                <div className="flex flex-col gap-1.5">
                  <Label>Location</Label>
                  <Combobox
                    options={ous}
                    value={draft.operationUnitId}
                    onValueChange={(id) => setDraft({ ...draft, operationUnitId: id })}
                    placeholder="Select location…"
                  />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="gate-name">Gate name</Label>
                <Input
                  id="gate-name"
                  value={draft.name}
                  maxLength={120}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Main gate"
                  className="h-12 rounded-xl text-base md:text-base"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="gate-desc">Description</Label>
                <Textarea
                  id="gate-desc"
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Where it is, timings…"
                  className="rounded-xl text-base md:text-base"
                />
              </div>
              {draft.id && (
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-3 text-sm">
                  <Checkbox
                    checked={draft.active}
                    onCheckedChange={(v) => setDraft({ ...draft, active: v === true })}
                  />
                  Active (an inactive gate is hidden from guards and from store subscriptions)
                </label>
              )}
            </div>
          )}
          <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <Button variant="outline" className="h-12 rounded-xl text-base" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              className="h-12 rounded-xl text-base"
              onClick={() => void saveDraft()}
              disabled={saving || !draft?.name.trim() || (!draft?.id && draft?.operationUnitId == null)}
            >
              {saving && <Loader2 className="animate-spin" />} Save
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </PageContainer>
  );
}

function GateCard({
  gate,
  showLocation,
  onEdit,
  onChanged,
}: {
  gate: AdminGate;
  showLocation: boolean;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const { token } = useAuth();
  const [adding, setAdding] = useState(false);

  async function act(p: Promise<{ errorMessage?: string } | null>, ok: string) {
    try {
      const r = await p;
      if (r?.errorMessage) {
        toast.error(r.errorMessage);
        return;
      }
      toast.success(ok);
      await onChanged();
    } catch (e) {
      toast.error(msg(e, "Something went wrong."));
    }
  }

  return (
    <Card className="rounded-2xl">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start gap-2">
          <DoorOpen className="mt-0.5 size-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold">{gate.name}</span>
              {!gate.active && <Badge variant="secondary">Inactive</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">
              {[showLocation ? gate.operationUnit?.name : null, gate.description].filter(Boolean).join(" · ")}
            </div>
          </div>
          <Button variant="ghost" size="icon-lg" aria-label="Edit gate" onClick={onEdit}>
            <Pencil />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon-lg" aria-label="Delete gate" className="text-destructive">
                <Trash2 />
              </Button>
            }
            title={`Delete ${gate.name}?`}
            description="Its guards and store subscriptions are removed. Arrivals already recorded at this gate stay in the log."
            confirmText="Delete gate"
            destructive
            onConfirm={() => void act(gateApi.deleteGate(token, gate.id), "Gate deleted.")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Guards
            <Button
              variant="outline"
              size="sm"
              className="ml-auto h-9 rounded-lg normal-case"
              onClick={() => setAdding((v) => !v)}
            >
              {adding ? <X /> : <UserPlus />} {adding ? "Close" : "Add guard"}
            </Button>
          </div>
          {adding && (
            <UserSearch
              exclude={gate.staff.map((s) => s.userId)}
              onPick={(u) => void act(gateApi.addStaff(token, gate.id, u.id), `${u.name} added to ${gate.name}.`)}
            />
          )}
          {gate.staff.length === 0 ? (
            <p className="text-xs text-muted-foreground">No guards yet.</p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {gate.staff.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-1 rounded-full border border-border py-0.5 pr-0.5 pl-3 text-sm"
                >
                  <span title={s.username}>{s.name}</span>
                  <ConfirmDialog
                    trigger={
                      <Button variant="ghost" size="icon-sm" className="rounded-full" aria-label={`Remove ${s.name}`}>
                        <X />
                      </Button>
                    }
                    title={`Remove ${s.name} from ${gate.name}?`}
                    confirmText="Remove"
                    destructive
                    onConfirm={() => void act(gateApi.removeStaff(token, s.id), `${s.name} removed.`)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">Stores subscribed</div>
          {gate.stores.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              None yet — a store subscribes from its operating profile in the Store App.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {gate.stores.map((s) => (
                <Badge key={s.id} variant="outline">
                  <Store /> {s.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** Server-side user search (multi-word, name or username) for adding a guard. */
function UserSearch({ exclude, onPick }: { exclude: number[]; onPick: (u: UserHit) => void }) {
  const { token } = useAuth();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<UserHit[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clear results for a short query
      setHits([]);
      return;
    }
    const controller = new AbortController();
    const h = setTimeout(() => {
      setLoading(true);
      gateApi
        .searchUsers(token, query, controller.signal)
        .then((d) => setHits(d?.users ?? []))
        .catch(() => {})
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 300);
    return () => {
      clearTimeout(h);
      controller.abort();
    };
  }, [q, token]);

  const shown = hits.filter((h) => !exclude.includes(h.id));

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border p-2">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          className="h-11 rounded-xl pl-8 text-base md:text-base"
        />
        {loading && (
          <Loader2 className="absolute top-1/2 right-2.5 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {shown.length > 0 && (
        <ul className="flex max-h-56 flex-col overflow-y-auto">
          {shown.map((u) => (
            <li key={u.id}>
              <Button
                variant="ghost"
                className="h-auto min-h-12 w-full justify-start px-2 py-2 text-left font-normal"
                onClick={() => onPick(u)}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm">{u.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{u.username}</span>
                </span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {q.trim().length >= 2 && !loading && shown.length === 0 && (
        <p className="px-2 text-xs text-muted-foreground">No matching users.</p>
      )}
    </div>
  );
}
